import * as React from "react";
import {useContext} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
import {Check, ContentCopy} from "@mui/icons-material";
import {AuthContext} from "./AuthContext";
import {formatDateTime} from "../utils/datetime";
import {cancelAllStripeSubscriptions, deleteAccount} from "../apis/junkyard";
import {DeleteAccountRequest, StripeCancelSubscriptionsRequest} from "../utils/typesStripe";
import {SubscriptionTier, subscriptionTierToDisplayName} from "../utils/types";

const formatTokenExpiry = (isoString: string): string => {
    const expiresAt = Date.parse(isoString);
    if (Number.isNaN(expiresAt)) {
        return "";
    }

    const msRemaining = expiresAt - Date.now();
    if (msRemaining <= 0) {
        return "Expired";
    }

    const minutes = Math.floor(msRemaining / (60 * 1000));
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const relative = hours > 0 ? `${hours} h ${remainingMinutes} min` : `${remainingMinutes} min`;

    return `${formatDateTime(isoString)} (in ${relative})`;
};

export const AccountProfile: React.FC = () => {
    const authContext = useContext(AuthContext);

    const [isCancellingSubscriptions, setIsCancellingSubscriptions] = React.useState<boolean>(false);
    const [isDeletingAccount, setIsDeletingAccount] = React.useState<boolean>(false);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState<boolean>(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState<boolean>(false);
    const [deleteResult, setDeleteResult] = React.useState<{ success: boolean; message: string } | null>(null);
    const [statusMessage, setStatusMessage] = React.useState<string | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [isTokenCopied, setIsTokenCopied] = React.useState<boolean>(false);

    const currentUser = authContext.getCurrentUser();
    const subscriptionTier = authContext.getSubscriptionTier();
    const accessToken = authContext.readAccessToken();
    const accessTokenExpires = localStorage.getItem("access_token_expires");

    const handleCopyAccessToken = async () => {
        if (!accessToken) {
            return;
        }

        try {
            await navigator.clipboard.writeText(accessToken);
            setIsTokenCopied(true);
            setTimeout(() => setIsTokenCopied(false), 2000);
        } catch (error) {
            console.error("Failed to copy access token", error);
        }
    };

    const formatSubscriptionTier = (tier: string): string => {
        return tier.charAt(0).toUpperCase() + tier.slice(1);
    };

    const handleCancelSubscriptions = async () => {
        const accessToken = authContext.readAccessToken();

        if (!accessToken) {
            setErrorMessage("You must be signed in to cancel subscriptions.");
            setStatusMessage(null);
            return;
        }

        setIsCancelDialogOpen(false);
        setIsCancellingSubscriptions(true);
        setErrorMessage(null);
        setStatusMessage(null);

        try {
            const result = await cancelAllStripeSubscriptions({
                access_token: accessToken,
            } as StripeCancelSubscriptionsRequest);

            if (result.num_cancel_failures > 0) {
                setErrorMessage(
                    `Cancelled ${result.num_cancel_successes} subscription(s), but failed to cancel ${result.num_cancel_failures}.`
                );
                return;
            }

            if (result.num_cancel_successes === 0) {
                setStatusMessage("No active subscriptions were found to cancel.");
                return;
            }

            setStatusMessage(`Successfully cancelled ${result.num_cancel_successes} subscription(s).`);
        } catch (error) {
            console.error("Failed to cancel all subscriptions", error);
            setErrorMessage("Failed to cancel subscriptions. Please try again.");
        } finally {
            setIsCancellingSubscriptions(false);
        }
    };

    const handleDeleteAccount = async () => {
        setIsDeletingAccount(true);
        setIsDeleteDialogOpen(false);

        try {
            const result = await deleteAccount({
                access_token: authContext.readAccessToken(),
            } as DeleteAccountRequest);

            setDeleteResult({ success: result.success, message: result.detail });
        } catch (error) {
            console.error("Failed to delete account", error);
            setDeleteResult({ success: false, message: "Failed to delete account. Please try again." });
        } finally {
            setIsDeletingAccount(false);
        }
    };

    if (!authContext.isAuthenticated()) {
        return (
            <Card sx={{maxWidth: 640, width: "100%"}}>
                <CardContent>
                    <Typography variant="h5" component="h1" gutterBottom>
                        Account Profile
                    </Typography>
                    <Alert severity="info">Please sign in to view your account profile.</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card sx={{maxWidth: 640, width: "100%"}}>
                <CardContent>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h5" component="h1" gutterBottom>
                                Account Profile
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage your account and subscription settings.
                            </Typography>
                        </Box>

                        <Divider />

                        <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                User
                            </Typography>
                            <Typography variant="body1">
                                {currentUser || "Unknown user"}
                            </Typography>
                        </Stack>

                        {authContext.getSubscriptionTier() === SubscriptionTier.premium &&
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Access Token
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Use this token to authenticate from your own machine, e.g. the local
                                WebSocket API.
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={accessToken ?? ""}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={isTokenCopied ? "Copied!" : "Copy access token"}>
                                                    <IconButton
                                                        onClick={handleCopyAccessToken}
                                                        size="small"
                                                        sx={{mr: -2}}
                                                        disabled={!accessToken}
                                                    >
                                                        {isTokenCopied
                                                            ? <Check fontSize="small"/>
                                                            : <ContentCopy fontSize="small"/>
                                                        }
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                            {accessTokenExpires && formatTokenExpiry(accessTokenExpires) && (
                                <Typography variant="body2" color="text.secondary">
                                    Expires: {formatTokenExpiry(accessTokenExpires)}
                                </Typography>
                            )}
                        </Stack>
                        }

                        <Stack direction={"column"} spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Subscription Tier
                            </Typography>
                            <Box>
                                <Stack direction="row" spacing={1} sx={{ padding: 2 }}>
                                    <Chip
                                        color={subscriptionTier === SubscriptionTier.free ? "primary" : "default"}
                                        label={subscriptionTierToDisplayName(SubscriptionTier.free)}
                                        variant={subscriptionTier === SubscriptionTier.free ? "filled" : "outlined"}
                                    />
                                    <Chip
                                        color={subscriptionTier === SubscriptionTier.plus ? "primary" : "default"}
                                        label={subscriptionTierToDisplayName(SubscriptionTier.plus) + " $5/month"}
                                        variant={subscriptionTier === SubscriptionTier.plus ? "filled" : "outlined"}
                                    />
                                    <Chip
                                        color={subscriptionTier === SubscriptionTier.premium ? "primary" : "default"}
                                        label={subscriptionTierToDisplayName(SubscriptionTier.premium) + " $10/month"}
                                        variant={subscriptionTier === SubscriptionTier.premium ? "filled" : "outlined"}
                                    />
                                </Stack>
                            </Box>
                        </Stack>

                        {statusMessage && (
                            <Alert severity="success">
                                {statusMessage}
                            </Alert>
                        )}

                        {errorMessage && (
                            <Alert severity="error">
                                {errorMessage}
                            </Alert>
                        )}
                    </Stack>
                </CardContent>

                <CardActions sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 1, p: 2}}>
                    {subscriptionTier !== SubscriptionTier.free && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setIsCancelDialogOpen(true)}
                            disabled={isCancellingSubscriptions || isDeletingAccount}
                            startIcon={isCancellingSubscriptions ? <CircularProgress size={16} /> : undefined}
                        >
                            {isCancellingSubscriptions ? "Cancelling subscriptions..." : "Cancel subscription"}
                        </Button>
                    )}

                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setIsDeleteDialogOpen(true)}
                        disabled={isCancellingSubscriptions || isDeletingAccount}
                        startIcon={isDeletingAccount ? <CircularProgress size={16} /> : undefined}
                        sx={{mt: subscriptionTier !== SubscriptionTier.free ? 2 : 0}}
                    >
                        {isDeletingAccount ? "Deleting account..." : "Delete account"}
                    </Button>
                </CardActions>
            </Card>

            <Dialog open={isCancelDialogOpen} onClose={() => setIsCancelDialogOpen(false)}>
                <DialogTitle>Cancel subscription?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will cancel all of your active subscriptions. Are you sure?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsCancelDialogOpen(false)}>Go back</Button>
                    <Button onClick={handleCancelSubscriptions}>Cancel subscription</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle>Delete account?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This action is permanent and cannot be undone. All subscriptions will be cancelled. Are you sure you want to delete your account?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteAccount}>Delete account</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteResult !== null} onClose={() => setDeleteResult(null)}>
                <DialogTitle>{deleteResult?.success ? "Account deleted" : "Deletion failed"}</DialogTitle>
                <DialogContent>
                    <Alert severity={deleteResult?.success ? "success" : "error"} sx={{mt: 1}}>
                        {deleteResult?.message}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteResult(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AccountProfile;
